// src/components/accordion/AccFeeling.tsx

import { ExpandMoreTwoTone } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Checkbox,
  Divider,
  List,
  Typography,
} from '@mui/material'
import { Dispatch, SetStateAction } from 'react'

import { FolderId, SensorRecord, SensorId } from '@/types/domain'

import { CorrelationFeelingItem, HStack } from '..'
import {
  colorSchemeFeeling,
  normalizedSensorType as nst,
  sensorType,
} from '../../api'
import { useFeelingSensors, useFolderSensors } from '../../hooks'

type AccFeelingProps = {
  data: {
    baseline: Array<any>
    selected: Array<SensorId>
  }
  id: FolderId
  onChange: Dispatch<SetStateAction<Array<SensorId>>>
  onChangeBaseline: Dispatch<SetStateAction<Array<SensorId>>>

  changeFeelingSensors: (id: SensorId) => void
  setFeelingSensorId: (id: SensorId) => void
}

export const AccFeeling = ({
  data,
  id,
  onChange,
  onChangeBaseline,

  changeFeelingSensors,
  setFeelingSensorId,
}: AccFeelingProps) => {
  const { baseline, selected } = data

  const feelingSensors: SensorRecord[] = useFeelingSensors(id)
  const fSensors: SensorRecord[] = useFolderSensors(id)

  const borderLeft = `solid 10px ${colorSchemeFeeling[0]}`

  const isSelected = (id: SensorId) => selected.includes(id)

  return ['temperature', 'co2', 'humidity', 'light'].map(medium => {
    const feelSensors = feelingSensors.filter(
      s => nst(s.sensor_type) === medium
    )

    const ALL_SELECTED = feelSensors.every(s => selected.includes(s.entity_id))
    const SOME_SELECTED = feelSensors.some(s => selected.includes(s.entity_id))

    const FILTER_MEDIUM = f =>
      nst(f.sensor_type, f.type_in_folder) === medium ||
      nst(f.sensor_type, f.type_in_folder) === `${medium}_detail`

    const onClick = event => {
      event.stopPropagation()

      let _selected = [...selected]
      let _baseline = [...baseline]

      feelSensors.forEach(s => {
        const sId: SensorId = Number(s.entity_id)

        if (!ALL_SELECTED) {
          _selected.push(sId)
        } else {
          _selected = _selected.filter(id => id !== sId)
          _baseline = _baseline.filter(f => f.entity_id !== sId)
        }
      })

      onChange(_selected)
      onChangeBaseline(_baseline)
    }

    return (
      feelingSensors.find(f => nst(f.sensor_type) === medium) && (
        <Accordion
          key={medium}
          sx={{ '&.Mui-expanded': { m: '0 !important' } }}
        >
          <AccordionSummary
            expandIcon={<ExpandMoreTwoTone />}
            sx={{ borderLeft, justifyContent: 'space-between' }}
          >
            <HStack justifyContent={'space-between'} sx={{ width: 1 }}>
              <Typography>{sensorType(medium)}</Typography>

              <Checkbox
                checked={ALL_SELECTED}
                color={'primary'}
                indeterminate={SOME_SELECTED && !ALL_SELECTED}
                onClick={onClick}
              />
            </HStack>
          </AccordionSummary>

          <AccordionDetails sx={{ borderLeft, p: 0 }}>
            <List sx={{ width: 1 }}>
              <Divider />

              {feelSensors.filter(FILTER_MEDIUM).map(s => {
                const sId: SensorId = Number(s.entity_id)

                const onClick = () => {
                  const record: SensorRecord = fSensors.find(
                    f => f.entity_id === sId
                  )
                  if (!record) return

                  setFeelingSensorId(sId)
                  changeFeelingSensors(sId)
                }

                return (
                  <CorrelationFeelingItem
                    id={sId}
                    key={sId}
                    onClick={onClick}
                    selected={isSelected(sId)}
                  />
                )
              })}
            </List>
          </AccordionDetails>
        </Accordion>
      )
    )
  })
}
